// CDC 2000 BMI-for-age LMS reference data (ages 2–20 years).
// Source: https://www.cdc.gov/growthcharts/data/zscore/bmiagerev.csv
// Sampled at yearly age points; L/M/S linearly interpolated for ages between samples.
// Sex: 'M' = male, 'F' = female.

type LMS = { ageMos: number; L: number; M: number; S: number };

const MALE_LMS: LMS[] = [
  { ageMos: 24,    L: -2.01118107,   M: 16.57502768, S: 0.080592465 },
  { ageMos: 36.5,  L: -1.419991255,  M: 16.00030401, S: 0.072634432 },
  { ageMos: 48.5,  L: -1.714869347,  M: 15.62817269, S: 0.071889214 },
  { ageMos: 60.5,  L: -2.61516595,   M: 15.41914163, S: 0.07599225  },
  { ageMos: 72.5,  L: -3.21170511,   M: 15.38353217, S: 0.083048178 },
  { ageMos: 84.5,  L: -3.323188896,  M: 15.51286936, S: 0.092131305 },
  { ageMos: 96.5,  L: -3.18305795,   M: 15.78231007, S: 0.102091189 },
  { ageMos: 108.5, L: -2.971148225,  M: 16.16712234, S: 0.111720691 },
  { ageMos: 120.5, L: -2.765648008,  M: 16.64613844, S: 0.120112464 },
  { ageMos: 132.5, L: -2.590560148,  M: 17.20088732, S: 0.126734613 },
  { ageMos: 144.5, L: -2.447426113,  M: 17.81463359, S: 0.131389042 },
  { ageMos: 156.5, L: -2.3294571,    M: 18.47179706, S: 0.13414147  },
  { ageMos: 168.5, L: -2.227362173,  M: 19.15758672, S: 0.135251083 },
  { ageMos: 180.5, L: -2.132344989,  M: 19.85766121, S: 0.135110159 },
  { ageMos: 192.5, L: -2.039015385,  M: 20.5576474,  S: 0.134198323 },
  { ageMos: 204.5, L: -1.949134561,  M: 21.24247982, S: 0.13305669  },
  { ageMos: 216.5, L: -1.874670324,  M: 21.8958685,  S: 0.132286382 },
  { ageMos: 228.5, L: -1.835138046,  M: 22.50071778, S: 0.132566359 },
  { ageMos: 240,   L: -1.84233016,   M: 23.02029424, S: 0.134539365 },
];

const FEMALE_LMS: LMS[] = [
  { ageMos: 24,    L: -0.98660853,   M: 16.42339664, S: 0.085451785 },
  { ageMos: 36.5,  L: -2.096828937,  M: 15.69924188, S: 0.078605255 },
  { ageMos: 48.5,  L: -3.018521987,  M: 15.29854897, S: 0.078713325 },
  { ageMos: 60.5,  L: -3.35007771,   M: 15.15188405, S: 0.084300139 },
  { ageMos: 72.5,  L: -3.225606516,  M: 15.21690296, S: 0.093803033 },
  { ageMos: 84.5,  L: -2.926186592,  M: 15.45356545, S: 0.105325289 },
  { ageMos: 96.5,  L: -2.617192204,  M: 15.82699517, S: 0.117158667 },
  { ageMos: 108.5, L: -2.360920527,  M: 16.30609316, S: 0.128013515 },
  { ageMos: 120.5, L: -2.171295888,  M: 16.86231366, S: 0.137057004 },
  { ageMos: 132.5, L: -2.045235058,  M: 17.46906585, S: 0.143868341 },
  { ageMos: 144.5, L: -1.975521156,  M: 18.10148804, S: 0.148361495 },
  { ageMos: 156.5, L: -1.954977947,  M: 18.73643338, S: 0.150705138 },
  { ageMos: 168.5, L: -1.977073595,  M: 19.35257209, S: 0.151255713 },
  { ageMos: 180.5, L: -2.034893091,  M: 19.9305662,  S: 0.150511645 },
  { ageMos: 192.5, L: -2.119156972,  M: 20.45325617, S: 0.14909006  },
  { ageMos: 204.5, L: -2.215737645,  M: 20.90575839, S: 0.147723315 },
  { ageMos: 216.5, L: -2.303687802,  M: 21.27532239, S: 0.14726877  },
  { ageMos: 228.5, L: -2.355677743,  M: 21.55082155, S: 0.148723546 },
  { ageMos: 240,   L: -2.34495843,   M: 21.71699934, S: 0.152974718 },
];

// Z-scores for the percentile cutoffs we plot.
const Z_P5  = -1.6449;
const Z_P85 = 1.0364;
const Z_P95 = 1.6449;

function interpolateLMS(table: LMS[], ageMos: number): LMS {
  if (ageMos <= table[0].ageMos) return table[0];
  if (ageMos >= table[table.length - 1].ageMos) return table[table.length - 1];
  for (let i = 0; i < table.length - 1; i++) {
    const a = table[i], b = table[i + 1];
    if (ageMos >= a.ageMos && ageMos <= b.ageMos) {
      const t = (ageMos - a.ageMos) / (b.ageMos - a.ageMos);
      return {
        ageMos,
        L: a.L + (b.L - a.L) * t,
        M: a.M + (b.M - a.M) * t,
        S: a.S + (b.S - a.S) * t,
      };
    }
  }
  return table[table.length - 1];
}

function bmiAtZ(lms: LMS, z: number): number {
  if (Math.abs(lms.L) < 1e-9) return lms.M * Math.exp(lms.S * z);
  return lms.M * Math.pow(1 + lms.L * lms.S * z, 1 / lms.L);
}

export type PediatricBMICutoffs = {
  underweight: number; // 5th percentile
  overweight: number;  // 85th percentile
  obese: number;       // 95th percentile
};

// Returns null if age/sex outside the 2–20 yr CDC reference window.
export function getPediatricBMICutoffs(
  ageYears: number,
  sex: 'M' | 'F',
): PediatricBMICutoffs | null {
  const ageMos = ageYears * 12;
  if (ageMos < 24 || ageMos > 240) return null;
  const table = sex === 'M' ? MALE_LMS : FEMALE_LMS;
  const lms = interpolateLMS(table, ageMos);
  return {
    underweight: round1(bmiAtZ(lms, Z_P5)),
    overweight:  round1(bmiAtZ(lms, Z_P85)),
    obese:       round1(bmiAtZ(lms, Z_P95)),
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
